import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnimatedSection from '../components/AnimatedSection';
import StaggeredList from '../components/StaggeredList';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Download, FileText, Video, Calendar, ExternalLink, Calculator, ChevronDown, ChevronRight, X, Play } from 'lucide-react';

const ResourceLibrarySandboxPage = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [videoModal, setVideoModal] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // Video data mapped by resource title
  const videoData = {
    "Cordflex® Overview": {
      youtubeId: "uxTUu8HbrEg",
      youtubeUrl: "https://youtu.be/uxTUu8HbrEg",
      duration: "2:55",
      available: true
    },
    "In-service Training Video - How to use Cordflex": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    },
    "Cordflex for Ambulation": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    },
    "Cordflex for Rotating a Patient in Bed": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    },
    "Cordflex for Transfers to Imaging": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    },
    "Cordflex for the NICU": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    },
    "Cordflex for the PICU": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    },
    "Cordflex for Specialty Lines": {
      youtubeId: null,
      youtubeUrl: null,
      duration: null,
      available: false
    }
  };

  const resourceSections = [
    {
      title: "Marketing & General Information",
      icon: <FileText className="h-5 w-5" />,
      resources: [
        { title: "Cordflex Clip Flyer", description: "Single-page overview highlighting key features and benefits", type: "Marketing Material", downloadable: true, available: false },
        { title: "Trifold Brochure", description: "Comprehensive trifold brochure for stakeholder presentations", type: "Marketing Material", downloadable: true, available: false },
        { title: "Med One Partnership Overview", description: "Information about our partnership with Med One for equipment solutions", type: "Partnership Info", downloadable: true, available: true, filePath: "/resources/Cordflex-Med One.pdf" },
        { title: "Versatility of Cordflex", description: "Documentation showcasing diverse applications across different care settings", type: "Use Case Guide", downloadable: true, available: true, filePath: "/resources/Versatility of Cordflex.pdf" }
      ]
    },
    {
      title: "Implementation & Service Tools",
      icon: <FileText className="h-5 w-5" />,
      resources: [
        { title: "Instructions for Use", description: "Current Instructions for Use (QSL-001 Rev 1). Also at nursetechmedical.com/ifu", type: "PDF Guide", downloadable: true, available: true, filePath: "/resources/Cordflex-IFU.pdf" },
        { title: "Sample Trial Survey & Feedback Form", description: "Collect feedback from your team after Cordflex implementation", type: "Form Template", downloadable: true, available: true, filePath: "/resources/Sample Feedback Form - Cordflex.docx" },
        { title: "Line Organization with Cordflex", description: "Guide to organizing and managing medical lines effectively with Cordflex", type: "Clinical Guide", downloadable: true, available: true, filePath: "/resources/Line Organization with Cordflex.pdf" }
      ]
    },
    {
      title: "Training & Explainer Videos",
      icon: <Video className="h-5 w-5" />,
      resources: [
        { title: "Cordflex Overview", description: "Complete introduction to the Cordflex system", type: "Video", downloadable: false, isVideo: true },
        { title: "In-service Training Video - How to use Cordflex", description: "Step-by-step training for nursing staff", type: "Training Video", downloadable: false, isVideo: true },
        { title: "Cordflex for Ambulation", description: "Specialized training for patient mobility applications", type: "Training Video", downloadable: false, isVideo: true },
        { title: "Cordflex for Rotating a Patient in Bed", description: "Bed positioning and patient turning procedures", type: "Training Video", downloadable: false, isVideo: true },
        { title: "Cordflex for Transfers to Imaging", description: "Optimizing patient transfers with line management", type: "Training Video", downloadable: false, isVideo: true },
        { title: "Cordflex for the NICU", description: "Specialized applications in neonatal intensive care", type: "Training Video", downloadable: false, isVideo: true },
        { title: "Cordflex for the PICU", description: "Pediatric ICU implementation guidelines", type: "Training Video", downloadable: false, isVideo: true },
        { title: "Cordflex for Specialty Lines", description: "Managing complex and specialty medical lines", type: "Training Video", downloadable: false, isVideo: true }
      ]
    },
    {
      title: "Research & Evidence-Based Practice Support",
      icon: <FileText className="h-5 w-5" />,
      resources: [
        { title: "Line Management for Reducing Trips and Falls", description: "Evidence-based strategies for preventing patient falls through better line management", type: "Clinical Guide", downloadable: true, available: false },
        { title: "Cordflex Infection Control and Line Management", description: "How adhesive and porous surfaces compromise patient safety — and why disposable, non-porous solutions matter", type: "Research Paper", downloadable: true, available: false },
        { title: "Cordflex Line Pullouts Prevention", description: "Clinical evidence on preventing accidental line dislodgements and the role of organized line management", type: "Research Paper", downloadable: true, available: false },
        { title: "Cordflex Line Tracing & Medication Safety", description: "Research on reducing line tracing errors and improving medication administration safety in critical care", type: "Research Paper", downloadable: true, available: false },
        { title: "Cordflex Background Research Whitepaper", description: "In-depth analysis of clinical evidence and research supporting Cordflex", type: "Research Paper", downloadable: true, available: false },
        { title: "Top 10 Health Tech Hazards 2025", description: "ECRI's 2025 list, including poorly managed infusion lines (#8). Opens ecri.org.", type: "Industry Report", downloadable: false, available: true, external: true, link: "https://home.ecri.org/blogs/ecri-thought-leadership-resources/techhazards2025" },
        { title: "Cordflex Value Analysis Framework", description: "Structured framework for conducting a value analysis of Cordflex in your facility", type: "PDF Analysis", downloadable: true, available: false },
        { title: "FAQ Document", description: "Common questions and answers about Cordflex implementation (File too large for web hosting - contact us for access)", type: "Reference Guide", downloadable: true, available: false }
      ]
    },
    {
      title: "Financial Analysis & ROI Tools",
      icon: <Calculator className="h-5 w-5" />,
      resources: [
        { title: "ROI Calculator", description: "Interactive tool to calculate return on investment for Cordflex", type: "Online Tool", downloadable: false, interactive: true, available: true, link: "/roi-calculator.html" },
        { title: "Cost Benefit Analysis of Using Cordflex", description: "Detailed financial impact analysis with references and ROI data", type: "PDF Analysis", downloadable: true, available: false },
        { title: "$1.7 Million Price Tag of Cord Disorganization", description: "Research document analyzing the financial and clinical impact of poor line management", type: "Research Paper", downloadable: true, available: false }
      ]
    },
    {
      title: "Committee Presentations",
      icon: <FileText className="h-5 w-5" />,
      resources: [
        { title: "Introducing Cordflex Presentation", description: "PowerPoint presentation for introducing Cordflex to committees and stakeholders", type: "PowerPoint", downloadable: true, available: false, externalLink: true },
        { title: "Value Analysis Committee Results", description: "Presentation template for sharing Cordflex trial results with value analysis committees", type: "PowerPoint", downloadable: true, available: false, externalLink: true }
      ]
    },
    {
      title: "Technical Reports",
      icon: <FileText className="h-5 w-5" />,
      resources: [
        { title: "Cordflex Technical Information", description: "Detailed specifications and engineering documentation", type: "Technical Document", downloadable: true, available: false },
        { title: "MSDS Data Sheet", description: "Material Safety Data Sheet for Cordflex components", type: "Safety Document", downloadable: true, available: false },
        { title: "Reporting Cordflex Issues", description: "Guidelines for reporting technical issues or adverse events", type: "Procedure Guide", downloadable: true, available: false }
      ]
    },
    {
      title: "Connect & Learn More",
      icon: <Calendar className="h-5 w-5" />,
      resources: [
        { title: "Schedule a Call", description: "Book a consultation with our team", type: "Consultation", downloadable: false, available: true, link: "https://calendly.com/davidusevitch/meet-with-david-nursetech-medical", external: true },
        { title: "Request a Sample", description: "Get a free sample kit for your ICU", type: "Sample Request", downloadable: false, available: true, link: "/sample-request" },
        { title: "Request a Trial", description: "Request a comprehensive trial in your facility", type: "Trial Request", downloadable: false, available: true, link: "/trial-request" }
      ]
    }
  ];

  const categoryLabels = [
    { key: 'All', label: 'All Resources' },
    { key: 'Marketing & General Information', label: 'Marketing' },
    { key: 'Implementation & Service Tools', label: 'Implementation' },
    { key: 'Training & Explainer Videos', label: 'Videos' },
    { key: 'Research & Evidence-Based Practice Support', label: 'Research' },
    { key: 'Financial Analysis & ROI Tools', label: 'Financial' },
    { key: 'Committee Presentations', label: 'Presentations' },
    { key: 'Technical Reports', label: 'Technical' },
    { key: 'Connect & Learn More', label: 'Connect' }
  ];

  const filteredSections = selectedCategory === 'All'
    ? resourceSections
    : resourceSections.filter(s => s.title === selectedCategory);

  const handleCategoryChange = (key) => {
    setSelectedCategory(key);
    // When switching to a specific category, expand it; when "All", expand all
    if (key === 'All') {
      const all = {};
      resourceSections.forEach((_, i) => { all[i] = true; });
      setExpandedSections(all);
    } else {
      const idx = resourceSections.findIndex(s => s.title === key);
      setExpandedSections({ [idx]: true });
    }
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleResourceClick = (resource) => {
    // Video resources open the modal
    if (resource.isVideo) {
      const vData = videoData[resource.title];
      setVideoModal({
        title: resource.title,
        description: resource.description,
        ...vData
      });
      return;
    }

    if (!resource.available) return;
    if (resource.downloadable && resource.filePath) {
      window.open(resource.filePath, '_blank');
    } else if (resource.interactive && resource.link) {
      window.open(resource.link, '_blank');
    } else if (resource.external) {
      window.open(resource.link, '_blank');
    } else if (resource.link) {
      window.location.href = resource.link;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Video':
      case 'Training Video': return { bg: '#e0f2fe', text: '#0369a1' };
      case 'Online Tool': return { bg: '#dcfce7', text: '#15803d' };
      case 'Consultation':
      case 'Sample Request':
      case 'Trial Request': return { bg: '#fef3c7', text: '#92400e' };
      case 'Research Paper':
      case 'Industry Report':
      case 'Clinical Guide': return { bg: '#ede9fe', text: '#6d28d9' };
      default: return { bg: '#DFEAF0', text: '#214140' };
    }
  };

  const getActionLabel = (resource) => {
    if (resource.isVideo) return 'Watch';
    if (!resource.available) return 'Coming Soon';
    if (resource.downloadable) return 'Download';
    if (resource.interactive) return 'Launch';
    if (resource.external) return 'Open';
    if (resource.link) return 'View';
    return 'Access';
  };

  const getActionIcon = (resource) => {
    if (resource.isVideo) return <Play className="h-3.5 w-3.5" />;
    if (resource.downloadable) return <Download className="h-3.5 w-3.5" />;
    if (resource.external) return <ExternalLink className="h-3.5 w-3.5" />;
    if (resource.interactive) return <Calculator className="h-3.5 w-3.5" />;
    return <ChevronRight className="h-3.5 w-3.5" />;
  };

  // Initialize all sections as expanded
  useEffect(() => {
    const initial = {};
    resourceSections.forEach((_, i) => { initial[i] = true; });
    setExpandedSections(initial);
  }, []);

  // Close modal on Escape
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setVideoModal(null); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24">
        {/* Compact Hero */}
        <section className="py-12 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-3xl lg:text-4xl font-bold mb-3" style={{ color: '#214140' }}>
                  Resource Library
                </h1>
                <p className="text-lg text-gray-600">
                  Implementation tools, training materials, research documents, and technical resources for your Cordflex journey.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Category Filter Tags */}
        <section className="py-6 sticky top-[64px] z-30 border-b border-gray-200" style={{ backgroundColor: '#DFEAF0' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {categoryLabels.map((cat) => {
                  const count = cat.key === 'All'
                    ? resourceSections.reduce((sum, s) => sum + s.resources.length, 0)
                    : (resourceSections.find(s => s.title === cat.key)?.resources.length || 0);
                  return (
                    <Badge
                      key={cat.key}
                      className="px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:scale-105"
                      style={{
                        backgroundColor: selectedCategory === cat.key ? '#214140' : '#8BBAB8',
                        color: 'white'
                      }}
                      onClick={() => handleCategoryChange(cat.key)}
                    >
                      {cat.label}
                      <span className="ml-1.5 opacity-75">({count})</span>
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Resource Sections */}
        <section className="py-10" style={{ backgroundColor: '#DFEAF0' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto space-y-4">
              {filteredSections.map((section) => {
                const sectionIndex = resourceSections.indexOf(section);
                return (
                <div key={section.title} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Section Header - Clickable */}
                  <button
                    onClick={() => toggleSection(sectionIndex)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors duration-150"
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#8BBAB8', color: 'white' }}
                      >
                        {section.icon}
                      </div>
                      <h2 className="text-lg font-bold text-left" style={{ color: '#214140' }}>
                        {section.title}
                      </h2>
                      <span className="text-xs text-gray-400 font-normal">
                        {section.resources.length} {section.resources.length === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                        expandedSections[sectionIndex] ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Resource Rows */}
                  {expandedSections[sectionIndex] && (
                    <div className="border-t border-gray-100">
                      {section.resources.map((resource, resourceIndex) => {
                        const typeColor = getTypeColor(resource.type);
                        const vData = resource.isVideo ? videoData[resource.title] : null;
                        const isUnavailableVideo = resource.isVideo && vData && !vData.available;
                        return (
                          <div
                            key={resourceIndex}
                            className={`flex items-center px-5 py-3 border-b border-gray-50 last:border-b-0 transition-colors duration-150 ${
                              isUnavailableVideo
                                ? 'opacity-50'
                                : resource.available !== false || resource.isVideo
                                  ? 'hover:bg-gray-50 cursor-pointer'
                                  : 'opacity-50'
                            }`}
                            onClick={() => {
                              if (isUnavailableVideo) return;
                              handleResourceClick(resource);
                            }}
                          >
                            {/* Title + Description */}
                            <div className="flex-1 min-w-0 mr-4">
                              <div className="flex items-center space-x-2 mb-0.5">
                                <h3 className="text-sm font-semibold truncate" style={{ color: '#214140' }}>
                                  {resource.title}
                                </h3>
                                {vData && (
                                  <span className="text-xs text-gray-400 flex-shrink-0">{vData.duration}</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate">
                                {resource.description}
                              </p>
                            </div>

                            {/* Type Badge */}
                            <Badge
                              className="text-xs px-2 py-0.5 mr-3 flex-shrink-0 font-medium"
                              style={{ backgroundColor: typeColor.bg, color: typeColor.text, border: 'none' }}
                            >
                              {resource.type}
                            </Badge>

                            {/* Action Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isUnavailableVideo || resource.available === false}
                              className={`flex-shrink-0 text-xs h-8 px-3 ${
                                (isUnavailableVideo || resource.available === false) ? 'cursor-not-allowed' : 'hover:bg-teal-50'
                              }`}
                              style={{
                                color: (isUnavailableVideo || resource.available === false) ? '#9ca3af' : '#214140'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isUnavailableVideo) return;
                                handleResourceClick(resource);
                              }}
                            >
                              {getActionIcon(resource)}
                              <span className="ml-1.5">
                                {isUnavailableVideo ? 'Coming Soon' : getActionLabel(resource)}
                              </span>
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Consultation Section */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center max-w-3xl mx-auto mb-6">
                <h2 className="text-2xl lg:text-3xl font-bold mb-3" style={{ color: '#214140' }}>
                  Schedule a Consultation
                </h2>
                <p className="text-gray-600">
                  Book a time to speak with our team about implementing Cordflex in your facility
                </p>
              </div>
              <div className="max-w-4xl mx-auto bg-gray-50 rounded-lg p-4 shadow-lg">
                <div
                  className="calendly-inline-widget"
                  data-url="https://calendly.com/davidusevitch/meet-with-david-nursetech-medical?hide_gdpr_banner=1&primary_color=214140"
                  style={{ minWidth: '320px', height: '650px' }}
                ></div>
              </div>
              <div className="text-center mt-8">
                <p className="text-gray-600 mb-4">Prefer another way to reach us?</p>
                <Button
                  variant="outline"
                  className="px-6 py-3 font-semibold border-2 hover:bg-teal-50 transition-colors duration-200"
                  style={{ borderColor: '#214140', color: '#214140' }}
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Support Team
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Quick Access - Full size matching original */}
        <section className="py-20" style={{ backgroundColor: '#273139' }}>
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white">
                  Quick Access
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  Frequently accessed resources and tools
                </p>
              </div>
            </AnimatedSection>

            <StaggeredList delay={200} className="grid md:grid-cols-4 gap-6">
              <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <FileText className="h-8 w-8 mx-auto mb-4" style={{ color: '#8BBAB8' }} />
                  <h3 className="font-semibold mb-2" style={{ color: '#214140' }}>Instructions for Use</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('/resources/Cordflex-IFU.pdf', '_blank')}
                  >
                    Download
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <Calculator className="h-8 w-8 mx-auto mb-4" style={{ color: '#8BBAB8' }} />
                  <h3 className="font-semibold mb-2" style={{ color: '#214140' }}>ROI Calculator</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('/roi-calculator.html', '_blank')}
                  >
                    Launch Tool
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <Video className="h-8 w-8 mx-auto mb-4" style={{ color: '#8BBAB8' }} />
                  <h3 className="font-semibold mb-2" style={{ color: '#214140' }}>Training Videos</h3>
                  <Button variant="outline" size="sm" onClick={() => window.location.href = '/resource-library'}>
                    View All
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-4" style={{ color: '#8BBAB8' }} />
                  <h3 className="font-semibold mb-2" style={{ color: '#214140' }}>Schedule Call</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('https://calendly.com/davidusevitch/meet-with-david-nursetech-medical', '_blank')}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            </StaggeredList>
          </div>
        </section>
      </main>
      <Footer />

      {/* Video Modal */}
      {videoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setVideoModal(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

          {/* Modal */}
          <div
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setVideoModal(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>

            {/* Video Area */}
            {videoModal.available && videoModal.youtubeId ? (
              <div className="aspect-video bg-black">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${videoModal.youtubeId}?autoplay=1&rel=0`}
                  title={videoModal.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                <div className="text-center">
                  <Video className="h-16 w-16 mx-auto mb-3 text-gray-400" />
                  <p className="text-lg font-semibold text-gray-600">Video Coming Soon</p>
                  <p className="text-sm text-gray-500 mt-1">This video is currently in production</p>
                </div>
              </div>
            )}

            {/* Info Area */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#214140' }}>
                    {videoModal.title}
                  </h3>
                  <p className="text-gray-600 mt-1">{videoModal.description}</p>
                </div>
                {videoModal.duration && (
                  <Badge
                    className="ml-3 flex-shrink-0"
                    style={{ backgroundColor: '#DFEAF0', color: '#214140', border: 'none' }}
                  >
                    {videoModal.duration}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-3 mt-4">
                <Button
                  size="sm"
                  className="font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#214140', color: 'white' }}
                  onClick={() => window.location.href = '/resource-library'}
                >
                  View All Videos
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceLibrarySandboxPage;
